import React, { useState } from 'react';
import type {
    TradingProfile,
    TradeFilter,
    BacktestReplenishmentRule,
    BacktestSortingRule,
    PriceAdjustmentRule,
    EntrySortingRule
} from '@einhaus/project314-types';
import { Disclosure } from '@/components/UI';
import { formatPositionSide, formatUnderlyingBias } from '@/utils/tradeSemantics';

interface TradingProfileDisplayProps {
    settings: TradingProfile;
}

const TradingProfileDisplay: React.FC<TradingProfileDisplayProps> = ({ settings }) => {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        tradeFilters: false,
        backtestReplenishmentRules: false,
        backtestSortingRules: false,
        entrySortingRules: false,
        brokerageOrderRules: true,
        parameterOverrides: false
    });

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Helper to render boolean values with colored badges
    const renderBoolean = (value: boolean) => (
        <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${value ? 'bg-green-600' : 'bg-red-500'} text-white`}>
            {value ? 'Yes' : 'No'}
        </span>
    );

    const backtestMetricScopeLabel =
        {
            entity: 'Entity Only',
            entityList: 'Strategy Entity List Cohort',
            allEntities: 'All Entities (Global Same Backtest Recipe)',
            blended: 'Blended Entity + Cohort'
        }[settings.backtestMetricScope] || settings.backtestMetricScope;
    const backtestSelectionScopeLabel = {
        entity: 'Entity Only',
        entityList: 'Strategy Entity List',
        allEntities: 'All Entities'
    }[settings.backtestSelectionScope ?? 'entity'];

    // Helper to render arrays with items
    const renderArray = <T,>(items: readonly T[] | null | undefined, renderer?: (item: T) => React.ReactNode) => {
        if (!items || items.length === 0) return <span className="text-zinc-500 italic">None</span>;

        return (
            <div className="flex flex-col gap-3">
                {items.map((item, index) => (
                    <div className="bg-[#2c2c2c] rounded-md p-3 border-l-3 border-l-[#1eacff]" key={index}>
                        {renderer ? renderer(item) : JSON.stringify(item)}
                    </div>
                ))}
            </div>
        );
    };

    // Render trade filter
    const renderTradeFilter = (filter: TradeFilter) => (
        <div className="flex flex-col gap-1">
            <div>
                <strong>ID:</strong> {filter.id}
            </div>
            <div>
                <strong>Field:</strong> {filter.field}
            </div>
            <div>
                <strong>Operator:</strong> {filter.operator}
            </div>
            <div>
                <strong>Value:</strong> {filter.value}
            </div>
            <div>
                <strong>Position side:</strong> {formatPositionSide(filter.tradeDirection)}
            </div>
        </div>
    );

    // Render backtest replenishment rule
    const renderReplenishmentRule = (rule: BacktestReplenishmentRule) => (
        <div className="flex flex-col gap-1">
            <div>
                <strong>ID:</strong> {rule.id}
            </div>
            <div>
                <strong>Balance Threshold Type:</strong>{' '}
                {rule.balanceThresholdType === 'percentageDecline' ? 'Percentage Decline' : 'Dollar Amount Decline'}
            </div>
            <div>
                <strong>Balance Threshold:</strong> {rule.balanceThreshold}
                {rule.balanceThresholdType === 'percentageDecline' ? '%' : '$'}
            </div>
            <div>
                <strong>Replenishment Type:</strong> {rule.replenishmentType === 'percentage' ? 'Percentage' : 'Dollar Amount'}
            </div>
            <div>
                <strong>Replenishment Amount:</strong> {rule.replenishmentAmount}
                {rule.replenishmentType === 'percentage' ? '%' : '$'}
            </div>
            <div>
                <strong>Max Replenishments:</strong> {rule.maxReplenishments}
            </div>
        </div>
    );

    // Render backtest sorting rule
    const renderSortingRule = (rule: BacktestSortingRule) => {
        // Map field names to display names
        const fieldDisplayName =
            {
                riskAdjustedScore: 'Risk-Adjusted Score',
                profitWeightedScore: 'Profit-Weighted Score',
                smartScore: 'Smart Score',
                avgTradeProfit: 'Average Trade Return (%)',
                profitFactor: 'Profit Factor',
                medianProfitPercent: 'Median Trade Return (%)',
                medianProfitLongPercent: 'Median Long Trade Return (%)',
                medianProfitShortPercent: 'Median Short Trade Return (%)',
                returnOnRiskCapital: 'Return on Risk Capital (%)',
                avgProfitPerDayPercent: 'Average Profit/Day (%)',
                medianProfitPerDayPercent: 'Median Profit/Day (%)',
                distinctTradingDays: 'Distinct Trading Days',
                activeMonths: 'Active Months',
                winRate: 'Win Rate',
                numTrades: 'Number of Trades'
            }[rule.field] || rule.field;

        return (
            <div className="flex flex-col gap-1">
                <div>
                    <strong>ID:</strong> {rule.id}
                </div>
                <div>
                    <strong>Field:</strong> {fieldDisplayName}
                </div>
                <div>
                    <strong>Order:</strong> {rule.order}
                </div>
                <div>
                    <strong>Position side:</strong> {formatPositionSide(rule.tradeDirection)}
                </div>
            </div>
        );
    };

    // Render entry sorting rule
    const renderEntrySortingRule = (rule: EntrySortingRule) => {
        const fieldDisplayName =
            new Map<string, string>([
                ['predictedProfit', 'Predicted Profit (%)'],
                ['avgTradeProfit', 'Average Trade Return (%)'],
                ['medianProfitPercent', 'Median Trade Return (%)'],
                ['medianProfitLongPercent', 'Median Long Trade Return (%)'],
                ['medianProfitShortPercent', 'Median Short Trade Return (%)'],
                ['avgProfitPerDayPercent', 'Average Profit Per Day (%)'],
                ['medianProfitPerDayPercent', 'Median Profit Per Day (%)'],
                ['profitFactor', 'Profit Factor'],
                ['returnOnRiskCapital', 'Return on Risk Capital (%)'],
                ['winRate', 'Win Rate'],
                ['numTrades', 'Number of Trades'],
                ['distinctTradingDays', 'Distinct Trading Days'],
                ['activeMonths', 'Active Months'],
                ['riskAdjustedScore', 'Risk-Adjusted Score'],
                ['profitWeightedScore', 'Profit-Weighted Score'],
                ['leg1.premiumArorc', 'Leg 1: Premium ARORC'],
                ['leg1.probabilityOTM', 'Leg 1: Probability OTM'],
                ['leg1.putSellScore', 'Leg 1: Put Sell Score'],
                ['leg2.premiumArorc', 'Leg 2: Premium ARORC'],
                ['leg2.probabilityOTM', 'Leg 2: Probability OTM'],
                ['leg2.putSellScore', 'Leg 2: Put Sell Score']
            ]).get(rule.field) || rule.field;

        return (
            <div className="flex flex-col gap-1">
                <div>
                    <strong>Priority:</strong> #{rule.priority}
                </div>
                <div>
                    <strong>Field:</strong> {fieldDisplayName}
                </div>
                <div>
                    <strong>Order:</strong> {rule.order === 'desc' ? 'Descending' : 'Ascending'}
                </div>
            </div>
        );
    };

    // Render price adjustment rule
    const renderPriceAdjustmentRule = (rule: PriceAdjustmentRule) => (
        <div className="flex flex-col gap-1">
            <div>
                <strong>ID:</strong> {rule.id}
            </div>
            <div>
                <strong>Trade Type:</strong> {rule.tradeType}
            </div>
            <div>
                <strong>Price Reference:</strong> {rule.priceReference ?? 'currentLimit'}
            </div>
            <div>
                <strong>Direction:</strong> {rule.direction}
            </div>
            <div>
                <strong>Adjustment:</strong>{' '}
                {rule.adjustmentUnit === 'ticks'
                    ? `${rule.adjustmentValue ?? rule.percentage} tick(s)`
                    : `${rule.adjustmentValue ?? rule.percentage}%`}
            </div>
            <div>
                <strong>Minutes:</strong> {rule.minutes}
            </div>
        </div>
    );

    return (
        <div className="text-zinc-100 bg-[#212529] rounded-lg p-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-[#495057] scrollbar-track-[#343a40] scrollbar-thumb-rounded-md scrollbar-track-rounded-md hover:scrollbar-thumb-[#6c757d]">
            <div className="flex justify-between items-center mb-6 pb-3 border-b border-[#343a40]">
                <h4 className="m-0 text-[#1eacff]">{settings.name}</h4>
                <div className="text-zinc-400 text-sm">ID: {settings.id}</div>
            </div>

            <div className="mb-6 pb-4 border-b border-[#343a40]">
                <h5 className="font-medium mb-3">General Settings</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Allowed Underlying Bias</div>
                        <div>{formatUnderlyingBias(settings.allowedUnderlyingBias)}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Backtest Metric Scope</div>
                        <div>{backtestMetricScopeLabel}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Best Backtest Selection Scope</div>
                        <div>{backtestSelectionScopeLabel}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Best Backtest Policy</div>
                        <div>{settings.backtestSelectionPolicyKey ?? 'profile_metric_v1'}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Selection Execution</div>
                        <div>{settings.backtestSelectionExecutionMode ?? 'authoritative'}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Min Option Price</div>
                        <div>
                            {settings.minOptionPrice !== null && settings.minOptionPrice !== undefined ? (
                                `$${settings.minOptionPrice}`
                            ) : (
                                <span className="text-zinc-500 italic">Not set</span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Deactivate on FOMC Days</div>
                        <div>{renderBoolean(settings.deactivateOnFomcDays)}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Auto-liquidate Option Settlement Shares</div>
                        <div>{renderBoolean(settings.autoLiquidateAssignedShares)}</div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="font-medium text-sm text-zinc-400">Option Settlement Liquidation Delay</div>
                        <div>
                            {settings.assignedShareLiquidationMinutesAfterOpen !== null &&
                            settings.assignedShareLiquidationMinutesAfterOpen !== undefined ? (
                                `${settings.assignedShareLiquidationMinutesAfterOpen} minutes`
                            ) : (
                                <span className="text-zinc-500 italic">At open</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-6 pb-4 border-b border-[#343a40]">
                <h5 className="font-medium mb-3">Allowed Entity Types</h5>
                <div>
                    {settings.allowedUnderlyingEntityTypes && settings.allowedUnderlyingEntityTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {settings.allowedUnderlyingEntityTypes.map((type, index) => (
                                <span className="inline-block text-xs font-medium px-2 py-1 rounded bg-p314-blue text-white" key={index}>
                                    {type}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className="text-zinc-500 italic">None specified</span>
                    )}
                </div>
            </div>

            <div className="mb-6 pb-4 border-b border-[#343a40]">
                <Disclosure
                    expanded={expandedSections.tradeFilters}
                    title={<h5 className="m-0 text-base font-medium text-white">Trade Filters</h5>}
                    variant="plain"
                    onToggle={() => toggleSection('tradeFilters')}
                >
                    {settings.tradeFilters && <div className="mt-3">{renderArray(settings.tradeFilters, renderTradeFilter)}</div>}
                </Disclosure>
            </div>

            <div className="mb-6 pb-4 border-b border-[#343a40]">
                <Disclosure
                    expanded={expandedSections.backtestReplenishmentRules}
                    title={<h5 className="m-0 text-base font-medium text-white">Backtest Replenishment Rules</h5>}
                    variant="plain"
                    onToggle={() => toggleSection('backtestReplenishmentRules')}
                >
                    {settings.backtestReplenishmentRules && (
                        <div className="mt-3">{renderArray(settings.backtestReplenishmentRules, renderReplenishmentRule)}</div>
                    )}
                </Disclosure>
            </div>

            <div className="mb-6 pb-4 border-b border-[#343a40]">
                <Disclosure
                    expanded={expandedSections.backtestSortingRules}
                    title={<h5 className="m-0 text-base font-medium text-white">Backtest Sorting Rules</h5>}
                    variant="plain"
                    onToggle={() => toggleSection('backtestSortingRules')}
                >
                    {settings.backtestSortingRules && (
                        <div className="mt-3">{renderArray(settings.backtestSortingRules, renderSortingRule)}</div>
                    )}
                </Disclosure>
            </div>

            <div className="mb-6 pb-4 border-b border-[#343a40]">
                <Disclosure
                    expanded={expandedSections.entrySortingRules}
                    title={<h5 className="m-0 text-base font-medium text-white">Entry Sorting Rules</h5>}
                    variant="plain"
                    onToggle={() => toggleSection('entrySortingRules')}
                >
                    <div className="mt-3">
                        {settings.entrySortingRules && settings.entrySortingRules.length > 0 ? (
                            renderArray(settings.entrySortingRules, renderEntrySortingRule)
                        ) : (
                            <span className="text-zinc-500 italic">Default sorting by average trade profit (descending)</span>
                        )}
                    </div>
                </Disclosure>
            </div>

            <div className="mb-6 pb-4 border-b border-[#343a40]">
                <Disclosure
                    expanded={expandedSections.brokerageOrderRules}
                    title={<h5 className="m-0 text-base font-medium text-white">Brokerage Order Rules</h5>}
                    variant="plain"
                    onToggle={() => toggleSection('brokerageOrderRules')}
                >
                    {settings.brokerageOrderRules && (
                        <div className="mt-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Cancel Unfilled Buy Orders At Close</div>
                                    <div>{renderBoolean(settings.brokerageOrderRules.cancelUnfilledBuyOrdersAtClose)}</div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Cancel Unfilled Sell Orders At Close</div>
                                    <div>{renderBoolean(settings.brokerageOrderRules.cancelUnfilledSellOrdersAtClose)}</div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Cancel Unfilled Buy Order Minutes</div>
                                    <div>
                                        {settings.brokerageOrderRules.cancelUnfilledBuyOrderMinutes !== null &&
                                        settings.brokerageOrderRules.cancelUnfilledBuyOrderMinutes !== undefined ? (
                                            `${settings.brokerageOrderRules.cancelUnfilledBuyOrderMinutes} minutes`
                                        ) : (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Cancel Unfilled Sell Order Minutes</div>
                                    <div>
                                        {settings.brokerageOrderRules.cancelUnfilledSellOrderMinutes !== null &&
                                        settings.brokerageOrderRules.cancelUnfilledSellOrderMinutes !== undefined ? (
                                            `${settings.brokerageOrderRules.cancelUnfilledSellOrderMinutes} minutes`
                                        ) : (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Buy Limit Target Percentage</div>
                                    <div>
                                        {settings.brokerageOrderRules.buyLimitTargetPercentage !== null &&
                                        settings.brokerageOrderRules.buyLimitTargetPercentage !== undefined ? (
                                            `${settings.brokerageOrderRules.buyLimitTargetPercentage}%`
                                        ) : (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Buy Limit Bid/Ask Above/Below</div>
                                    <div>
                                        {settings.brokerageOrderRules.buyLimitBidAskAboveBelow || (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Buy Limit Bid/Ask Target</div>
                                    <div>
                                        {settings.brokerageOrderRules.buyLimitBidAskTarget || (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Sell Limit Target Percentage</div>
                                    <div>
                                        {settings.brokerageOrderRules.sellLimitTargetPercentage !== null &&
                                        settings.brokerageOrderRules.sellLimitTargetPercentage !== undefined ? (
                                            `${settings.brokerageOrderRules.sellLimitTargetPercentage}%`
                                        ) : (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Sell Limit Bid/Ask Above/Below</div>
                                    <div>
                                        {settings.brokerageOrderRules.sellLimitBidAskAboveBelow || (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="font-medium text-sm text-zinc-400">Sell Limit Bid/Ask Target</div>
                                    <div>
                                        {settings.brokerageOrderRules.sellLimitBidAskTarget || (
                                            <span className="text-zinc-500 italic">Not set</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4">
                                <h6 className="font-medium text-base mb-2">Price Adjustment Rules</h6>
                                {settings.brokerageOrderRules.priceAdjustmentRules &&
                                settings.brokerageOrderRules.priceAdjustmentRules.length > 0 ? (
                                    renderArray(settings.brokerageOrderRules.priceAdjustmentRules, renderPriceAdjustmentRule)
                                ) : (
                                    <span className="text-zinc-500 italic">None</span>
                                )}
                            </div>
                        </div>
                    )}
                </Disclosure>
            </div>
        </div>
    );
};

export default TradingProfileDisplay;
